import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { createSelector, Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { AddTicketMealPlan } from 'src/app/store/tickets/tickets.action';
import { NormalizedDiner } from 'src/app/models';
import { DinersSelectors } from 'src/app/store/diners/diners.selectors';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslocoService } from '@ngneat/transloco';

class PageSelector {
  static mealPlanDiner = createSelector(
    [DinersSelectors.dinersById, TicketsStateSelectors.normalizedTicket],
    (dinersById, { diner_id, guest_of_diner_id }) =>
      dinersById[diner_id || guest_of_diner_id]
  );
}

@Component({
  templateUrl: './meal-plan.modal.html',
  styleUrls: ['./meal-plan.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MealPlanModal {
  @ViewSelectSnapshot(PageSelector.mealPlanDiner)
  readonly diner: NormalizedDiner;
  @ViewSelectSnapshot(LocationSelectors.getIsServiceAppointment)
  readonly isServiceAppointment: boolean;

  constructor(
    private readonly modalController: ModalController,
    private readonly store: Store,
    private readonly toastService: ToastService,
    private readonly translocoService: TranslocoService
  ) {}

  onPayForMeal() {
    this.modalController.dismiss();
  }

  onCloseModal() {
    this.modalController.dismiss();
  }

  /**
   * POS-972 On-Screen Signature — Meal Plan.
   * `assign_mealplan` never creates the `Pos::MealPlan::Transaction` itself — that only happens
   * at ticket close (`payout_mealplan_balance`, called from `close_ticket`), so there is nothing
   * to check for a signature here. Signature capture for meal plan happens entirely at Close
   * Ticket time, same place as bill-to-room/charge account (see checkout.page.ts/payments.page.ts).
   */
  async onUseMealPlan(mealPlanId: number) {
    const ticketUuid = this.store.selectSnapshot(
      TicketsStateSelectors.selectedTicketId
    );

    try {
      await firstValueFrom(
        this.store.dispatch(new AddTicketMealPlan({ mealPlanId, ticketUuid }))
      );
    } catch {
      // Leave the modal open so the operator can retry rather than stranding them with a
      // silently-rejected promise and no feedback.
      this.toastService.error(
        this.translocoService.translate('errors.naError')
      );
      return;
    }

    this.modalController.dismiss();
  }
}
